import {
  ENTITY_ORDER,
  FINANCIAL_FIELD_GROUPS,
  INPUT_SCHEMAS,
  newId,
  serverOwnedDefaults,
  type Change,
  type EntityEnvelope,
  type EntityType,
  type OperationResult,
  type SyncOperation,
  type WritableEntityType,
} from '@fd/schemas';
import type { SqlDriver, SqlExecutor, SqlStatement, SqlValue } from './driver.ts';
import { dateKeyOf } from './optimistic.ts';
import { migrateLocal } from './schema.ts';
import { TransportError, type SyncTransport } from './transport.ts';

export type EntitySyncState = 'synced' | 'pending' | 'syncing' | 'failed' | 'conflict';

export type LocalRecord = Record<string, unknown> & {
  id: string;
  householdId: string;
  version: number;
  syncState: EntitySyncState;
};

export type EngineStatus = {
  readonly online: boolean;
  readonly syncing: boolean;
  readonly queued: number;
  readonly failed: number;
  readonly conflicts: number;
  readonly lastSuccessAt: string | null;
  readonly lastError: string | null;
};

export type ChangeEvent = { readonly entityTypes: ReadonlySet<EntityType> };

type EntityRow = {
  entity_type: EntityType;
  id: string;
  household_id: string;
  version: number;
  deleted: number;
  data: string | null;
  local: string | null;
  local_deleted: number;
  sync_state: EntitySyncState;
};

type OutboxRow = {
  seq: number;
  operation_id: string;
  household_id: string;
  entity_type: EntityType;
  entity_id: string;
  action: SyncOperation['action'];
  base_version: number | null;
  payload: string | null;
  base: string | null;
  created_at: string;
  status: 'queued' | 'inflight' | 'failed';
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
};

type NewOutboxRow = Omit<OutboxRow, 'seq' | 'status' | 'attempts' | 'next_attempt_at' | 'last_error'>;

export type ConflictRecord = {
  id: string;
  householdId: string;
  entityType: EntityType;
  entityId: string;
  reason: string;
  fields: string[];
  serverEntity: Record<string, unknown> | null;
  localState: Record<string, unknown> | null;
  /** The fields the user changed (what "use my version" applies). */
  localPatch: Record<string, unknown> | null;
  createdAt: string;
};

export type FailedOperation = {
  operationId: string;
  entityType: EntityType;
  entityId: string;
  action: SyncOperation['action'];
  error: { code: string; message: string } | null;
};

const MAX_TRANSIENT_ATTEMPTS = 8;
const PUSH_BATCH = 100;
const PULL_PAGE = 500;

const parse = (text: string | null | undefined): Record<string, unknown> | null =>
  text === null || text === undefined ? null : (JSON.parse(text) as Record<string, unknown>);

function canonical(value: unknown): string {
  const sort = (v: unknown): unknown =>
    Array.isArray(v)
      ? v.map(sort)
      : v !== null && typeof v === 'object'
        ? Object.fromEntries(
            Object.keys(v as object)
              .sort()
              .map((k) => [k, sort((v as Record<string, unknown>)[k])]),
          )
        : v === undefined
          ? null
          : v;
  return JSON.stringify(sort(value));
}
const same = (a: unknown, b: unknown) => canonical(a) === canonical(b);

export class ValidationError extends Error {
  readonly issues: readonly { path: string; message: string }[];
  constructor(issues: readonly { path: string; message: string }[]) {
    super(issues.map((i) => `${i.path}: ${i.message}`).join('; '));
    this.name = 'ValidationError';
    this.issues = issues;
  }
}

export type SyncEngineOptions = {
  readonly driver: SqlDriver;
  readonly transport: SyncTransport;
  readonly deviceId: string;
  readonly now?: () => Date;
  readonly random?: () => number;
};

/**
 * Local-first sync client (SYNC_PROTOCOL §9–10). A local write lands in the entity table and
 * the outbox in one SQLite transaction; push sends the outbox, pull applies the server change
 * log. What the screens read is always "canonical state + changes still waiting".
 */
export class SyncEngine {
  private readonly driver: SqlDriver;
  private readonly transport: SyncTransport;
  private readonly deviceId: string;
  private readonly now: () => Date;
  private readonly random: () => number;
  private readonly listeners = new Set<(event: ChangeEvent) => void>();
  private readonly statusListeners = new Set<(status: EngineStatus) => void>();
  private status: EngineStatus = {
    online: true,
    syncing: false,
    queued: 0,
    failed: 0,
    conflicts: 0,
    lastSuccessAt: null,
    lastError: null,
  };
  private running: Promise<void> | null = null;
  private rerun = false;

  constructor(options: SyncEngineOptions) {
    this.driver = options.driver;
    this.transport = options.transport;
    this.deviceId = options.deviceId;
    this.now = options.now ?? (() => new Date());
    this.random = options.random ?? Math.random;
  }

  async start(): Promise<void> {
    await migrateLocal(this.driver);
    const rows = await this.driver.all<{ value: string }>(`select value from meta where key = 'lastSuccessAt'`);
    this.status = { ...this.status, lastSuccessAt: rows[0]?.value ?? null };
    await this.refreshCounts();
  }

  // ------------------------------------------------------------------------------ subscriptions

  subscribe(listener: (event: ChangeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeStatus(listener: (status: EngineStatus) => void): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): EngineStatus {
    return this.status;
  }

  private emit(types: Iterable<EntityType>): void {
    const set = new Set(types);
    if (set.size === 0) return;
    for (const listener of this.listeners) listener({ entityTypes: set });
  }

  private setStatus(patch: Partial<EngineStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const listener of this.statusListeners) listener(this.status);
  }

  private async refreshCounts(): Promise<void> {
    const [counts] = await this.driver.all<{ queued: number | null; failed: number | null }>(
      `select sum(case when status <> 'failed' then 1 else 0 end) as queued,
              sum(case when status = 'failed' then 1 else 0 end) as failed from outbox`,
    );
    const [conflicts] = await this.driver.all<{ n: number }>(`select count(*) as n from conflicts`);
    this.setStatus({
      queued: Number(counts?.queued ?? 0),
      failed: Number(counts?.failed ?? 0),
      conflicts: Number(conflicts?.n ?? 0),
    });
  }

  // ------------------------------------------------------------------------------ reads

  private toRecord(row: EntityRow): LocalRecord | null {
    const effective = row.local !== null ? parse(row.local) : parse(row.data);
    const deleted = row.local !== null ? row.local_deleted === 1 : row.deleted === 1;
    if (!effective || deleted) return null;
    return {
      ...effective,
      id: row.id,
      householdId: row.household_id,
      version: row.version,
      syncState: row.sync_state,
    } as LocalRecord;
  }

  async get(entityType: EntityType, id: string): Promise<LocalRecord | null> {
    const row = await this.loadRow(this.driver, entityType, id);
    return row ? this.toRecord(row) : null;
  }

  async list(
    householdId: string,
    entityType: EntityType,
    options: { from?: string; to?: string; limit?: number; newestFirst?: boolean } = {},
  ): Promise<LocalRecord[]> {
    const where = ['household_id = ?', 'entity_type = ?'];
    const params: SqlValue[] = [householdId, entityType];
    if (options.from) {
      where.push('date_key >= ?');
      params.push(options.from);
    }
    if (options.to) {
      where.push('date_key <= ?');
      params.push(options.to);
    }
    const order = options.newestFirst ? 'order by date_key desc, id desc' : 'order by date_key, id';
    const limit = options.limit ? `limit ${Math.floor(options.limit)}` : '';
    const rows = await this.driver.all<EntityRow>(
      `select * from entities where ${where.join(' and ')} ${order} ${limit}`,
      params,
    );
    return rows.map((row) => this.toRecord(row)).filter((r): r is LocalRecord => r !== null);
  }

  async households(): Promise<LocalRecord[]> {
    const rows = await this.driver.all<EntityRow>(`select * from entities where entity_type = 'household'`);
    return rows.map((row) => this.toRecord(row)).filter((r): r is LocalRecord => r !== null);
  }

  // ------------------------------------------------------------------------------ internals

  private async loadRow(db: SqlExecutor, entityType: EntityType, id: string): Promise<EntityRow | null> {
    const rows = await db.all<EntityRow>(`select * from entities where entity_type = ? and id = ?`, [entityType, id]);
    return rows[0] ?? null;
  }

  private opsFor(db: SqlExecutor, entityType: EntityType, id: string): Promise<OutboxRow[]> {
    return db.all<OutboxRow>(`select * from outbox where entity_type = ? and entity_id = ? order by seq`, [entityType, id]);
  }

  /** Canonical state with every change still waiting in the outbox applied on top. */
  private replay(entityType: EntityType, canonicalData: Record<string, unknown> | null, ops: readonly OutboxRow[]) {
    let state = canonicalData ? { ...canonicalData } : null;
    let deleted = false;
    for (const op of ops) {
      const payload = parse(op.payload) ?? {};
      if (op.action === 'create') {
        state = {
          ...serverOwnedDefaults(entityType, payload),
          ...payload,
          id: op.entity_id,
          householdId: op.household_id,
          createdAt: op.created_at,
          updatedAt: op.created_at,
          deletedAt: null,
        };
        deleted = false;
      } else if (op.action === 'update' && state) {
        state = { ...state, ...payload };
      } else if (op.action === 'delete') {
        deleted = true;
      }
    }
    return { state, deleted };
  }

  /** Recomputes the effective local state of one record from its canonical data and outbox. */
  private async recompute(db: SqlExecutor, entityType: EntityType, id: string, householdId: string): Promise<void> {
    const row = await this.loadRow(db, entityType, id);
    const ops = await this.opsFor(db, entityType, id);
    if (!row && ops.length === 0) return;
    if (row && row.data === null && row.version === 0 && ops.length === 0) {
      await db.run(`delete from entities where entity_type = ? and id = ?`, [entityType, id]);
      return;
    }
    const data = parse(row?.data);
    const deletedCanonical = row?.deleted === 1;
    const conflict = await db.all<{ id: string }>(`select id from conflicts where entity_type = ? and entity_id = ?`, [entityType, id]);
    let local: string | null = null;
    let localDeleted = 0;
    let state: EntitySyncState = conflict.length > 0 ? 'conflict' : 'synced';
    let effective = deletedCanonical ? null : data;
    if (ops.length > 0) {
      const replayed = this.replay(entityType, deletedCanonical ? null : data, ops);
      localDeleted = replayed.deleted || replayed.state === null ? 1 : 0;
      local = JSON.stringify(replayed.state ?? {});
      effective = replayed.state;
      state = ops.some((op) => op.status === 'failed')
        ? 'failed'
        : ops.some((op) => op.status === 'inflight')
          ? 'syncing'
          : 'pending';
    }
    await db.run(
      `insert into entities (entity_type, id, household_id, version, deleted, data, local, local_deleted, sync_state, date_key, updated_at)
       values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       on conflict (entity_type, id) do update set household_id = excluded.household_id, version = excluded.version,
         deleted = excluded.deleted, data = excluded.data, local = excluded.local, local_deleted = excluded.local_deleted,
         sync_state = excluded.sync_state, date_key = excluded.date_key, updated_at = excluded.updated_at`,
      [
        entityType,
        id,
        row?.household_id ?? householdId,
        row?.version ?? 0,
        deletedCanonical ? 1 : 0,
        row?.data ?? null,
        local,
        localDeleted,
        state,
        dateKeyOf(entityType, effective),
        this.now().toISOString(),
      ],
    );
  }

  private async enqueue(db: SqlExecutor, op: NewOutboxRow): Promise<void> {
    await db.run(
      `insert into outbox (operation_id, household_id, entity_type, entity_id, action, base_version, payload, base, created_at)
       values (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [op.operation_id, op.household_id, op.entity_type, op.entity_id, op.action, op.base_version, op.payload, op.base, op.created_at],
    );
  }

  private async storeCanonical(db: SqlExecutor, entity: EntityEnvelope): Promise<void> {
    await db.run(
      `insert into entities (entity_type, id, household_id, version, deleted, data, sync_state, updated_at)
       values (?, ?, ?, ?, ?, ?, 'synced', ?)
       on conflict (entity_type, id) do update set version = excluded.version, deleted = excluded.deleted, data = excluded.data`,
      [
        entity.entityType,
        entity.entityId,
        entity.householdId,
        entity.version,
        entity.deleted ? 1 : 0,
        entity.data ? JSON.stringify(entity.data) : null,
        this.now().toISOString(),
      ],
    );
  }

  // ------------------------------------------------------------------------------ local writes

  /** Creates a record locally and queues it. Validated here too, for instant feedback. */
  async create(householdId: string, entityType: WritableEntityType, id: string, payload: Record<string, unknown>): Promise<void> {
    const parsed = INPUT_SCHEMAS[entityType].safeParse(payload);
    if (!parsed.success) {
      throw new ValidationError(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
    }
    await this.driver.transaction(async (tx) => {
      if (await this.loadRow(tx, entityType, id)) throw new Error(`${entityType} ${id} already exists locally`);
      await this.enqueue(tx, {
        operation_id: newId(),
        household_id: householdId,
        entity_type: entityType,
        entity_id: id,
        action: 'create',
        base_version: null,
        payload: JSON.stringify(parsed.data),
        base: null,
        created_at: this.now().toISOString(),
      });
      await this.recompute(tx, entityType, id, householdId);
    });
    await this.refreshCounts();
    this.emit([entityType]);
  }

  /** Changes fields of a record; only real changes are queued, with the values they replace. */
  async update(householdId: string, entityType: WritableEntityType, id: string, patch: Record<string, unknown>): Promise<void> {
    let changed = false;
    await this.driver.transaction(async (tx) => {
      const row = await this.loadRow(tx, entityType, id);
      const current = row ? this.toRecord(row) : null;
      if (!row || !current) throw new Error(`${entityType} ${id} not found`);
      const inputKeys = Object.keys(INPUT_SCHEMAS[entityType].shape);
      const changes = Object.fromEntries(
        Object.entries(patch).filter(([key, value]) => inputKeys.includes(key) && !same(current[key], value)),
      );
      if (Object.keys(changes).length === 0) return;
      const candidate = Object.fromEntries(inputKeys.map((key) => [key, key in changes ? changes[key] : current[key]]));
      const parsed = INPUT_SCHEMAS[entityType].safeParse(candidate);
      if (!parsed.success) {
        throw new ValidationError(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
      }
      changed = true;
      const ops = await this.opsFor(tx, entityType, id);
      const pendingCreate = ops.find((op) => op.action === 'create' && op.status === 'queued');
      const [last] = await tx.all<{ seq: number }>(`select max(seq) as seq from outbox`);
      // Fold into the unsent create only when nothing was queued after it: otherwise the change
      // may depend on records created in between (e.g. the owner person of a new household).
      if (pendingCreate && pendingCreate.seq === last?.seq) {
        // Not sent yet: fold the change into the create instead of queueing an update.
        const payload = { ...(parse(pendingCreate.payload) ?? {}), ...changes };
        await tx.run(`update outbox set payload = ? where seq = ?`, [JSON.stringify(payload), pendingCreate.seq]);
      } else {
        const base: Record<string, unknown> = {};
        for (const key of Object.keys(changes)) base[key] = current[key] ?? null;
        for (const group of FINANCIAL_FIELD_GROUPS[entityType] ?? []) {
          if (Object.keys(changes).some((key) => group.includes(key))) {
            for (const key of group) base[key] = current[key] ?? null;
          }
        }
        await this.enqueue(tx, {
          operation_id: newId(),
          household_id: householdId,
          entity_type: entityType,
          entity_id: id,
          action: 'update',
          base_version: Math.max(row.version, 1),
          payload: JSON.stringify(changes),
          base: JSON.stringify(base),
          created_at: this.now().toISOString(),
        });
      }
      await this.recompute(tx, entityType, id, householdId);
    });
    if (!changed) return;
    await this.refreshCounts();
    this.emit([entityType]);
  }

  async remove(householdId: string, entityType: WritableEntityType, id: string): Promise<void> {
    await this.driver.transaction(async (tx) => {
      const row = await this.loadRow(tx, entityType, id);
      if (!row) return;
      const ops = await this.opsFor(tx, entityType, id);
      const pendingCreate = ops.find((op) => op.action === 'create' && op.status === 'queued');
      if (pendingCreate && row.version === 0) {
        // Never reached the server: forget it entirely.
        await tx.run(`delete from outbox where entity_type = ? and entity_id = ?`, [entityType, id]);
        await tx.run(`delete from entities where entity_type = ? and id = ?`, [entityType, id]);
        return;
      }
      const current = this.toRecord(row);
      await this.enqueue(tx, {
        operation_id: newId(),
        household_id: householdId,
        entity_type: entityType,
        entity_id: id,
        action: 'delete',
        base_version: Math.max(row.version, 1),
        payload: null,
        base: current ? JSON.stringify(current) : null,
        created_at: this.now().toISOString(),
      });
      await this.recompute(tx, entityType, id, householdId);
    });
    await this.refreshCounts();
    this.emit([entityType]);
  }

  // ------------------------------------------------------------------------------ sync

  /** Push then pull. Never runs twice at once; a call during a run schedules one more run. */
  sync(householdIds: readonly string[]): Promise<void> {
    if (this.running) {
      this.rerun = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.rerun = false;
          this.setStatus({ syncing: true });
          await this.pushPending();
          for (const householdId of householdIds) await this.pull(householdId);
          const at = this.now().toISOString();
          await this.driver.run(
            `insert into meta (key, value) values ('lastSuccessAt', ?) on conflict (key) do update set value = excluded.value`,
            [at],
          );
          this.setStatus({ online: true, lastSuccessAt: at, lastError: null });
        } while (this.rerun);
      } catch (error) {
        const offline = error instanceof TransportError && error.retryable;
        this.setStatus({ online: !offline, lastError: error instanceof Error ? error.message : String(error) });
        if (!(error instanceof TransportError)) throw error;
      } finally {
        this.running = null;
        this.setStatus({ syncing: false });
        await this.refreshCounts();
      }
    })();
    return this.running;
  }

  private backoffMs(attempts: number): number {
    const cap = Math.min(300_000, 1000 * 2 ** attempts);
    return Math.floor(this.random() * cap);
  }

  /** Sends queued operations in order. Records with a failed or conflicting change wait. */
  async pushPending(): Promise<void> {
    for (;;) {
      const nowIso = this.now().toISOString();
      const candidates = await this.driver.all<OutboxRow>(`select * from outbox order by seq`);
      const conflicted = new Set(
        (await this.driver.all<{ entity_type: string; entity_id: string }>(`select entity_type, entity_id from conflicts`)).map(
          (c) => `${c.entity_type}:${c.entity_id}`,
        ),
      );
      const blocked = new Set<string>();
      const batch: OutboxRow[] = [];
      for (const op of candidates) {
        const key = `${op.entity_type}:${op.entity_id}`;
        if (blocked.has(key) || conflicted.has(key)) continue;
        if (op.status !== 'queued' || (op.next_attempt_at !== null && op.next_attempt_at > nowIso)) {
          blocked.add(key); // keep the order of changes to one record
          continue;
        }
        batch.push(op);
        if (batch.length >= PUSH_BATCH) break;
      }
      if (batch.length === 0) return;

      await this.driver.transaction(async (tx) => {
        for (const op of batch) {
          await tx.run(`update outbox set status = 'inflight' where seq = ?`, [op.seq]);
          await this.recompute(tx, op.entity_type, op.entity_id, op.household_id);
        }
      });
      this.emit(batch.map((op) => op.entity_type));

      let results: OperationResult[];
      try {
        const response = await this.transport.push({
          deviceId: this.deviceId,
          operations: batch.map((op) => ({
            operationId: op.operation_id,
            householdId: op.household_id,
            entityType: op.entity_type,
            entityId: op.entity_id,
            action: op.action,
            baseVersion: op.base_version,
            payload: parse(op.payload),
            base: parse(op.base),
            createdAt: op.created_at,
          })),
        });
        results = response.results;
      } catch (error) {
        const retryable = !(error instanceof TransportError) || error.retryable;
        await this.driver.transaction(async (tx) => {
          for (const op of batch) {
            const attempts = op.attempts + 1;
            const failed = !retryable || attempts >= MAX_TRANSIENT_ATTEMPTS;
            await tx.run(`update outbox set status = ?, attempts = ?, next_attempt_at = ?, last_error = ? where seq = ?`, [
              failed ? 'failed' : 'queued',
              attempts,
              new Date(this.now().getTime() + this.backoffMs(attempts)).toISOString(),
              JSON.stringify({
                code: error instanceof TransportError ? error.code : 'NETWORK',
                message: error instanceof Error ? error.message : String(error),
              }),
              op.seq,
            ]);
            await this.recompute(tx, op.entity_type, op.entity_id, op.household_id);
          }
        });
        this.emit(batch.map((op) => op.entity_type));
        await this.refreshCounts();
        throw error;
      }

      const touched = new Set<EntityType>();
      await this.driver.transaction(async (tx) => {
        for (const result of results) {
          const op = batch.find((o) => o.operation_id === result.operationId);
          if (!op) continue;
          touched.add(op.entity_type);
          await this.applyResult(tx, op, result);
        }
      });
      this.emit(touched);
      await this.refreshCounts();
    }
  }

  private async applyResult(db: SqlExecutor, op: OutboxRow, result: OperationResult): Promise<void> {
    switch (result.status) {
      case 'applied':
      case 'duplicate': {
        await db.run(`delete from outbox where seq = ?`, [op.seq]);
        if (result.entity) {
          const existing = await this.loadRow(db, op.entity_type, op.entity_id);
          // A newer canonical version may already have arrived through pull.
          if (!existing || existing.version <= result.entity.version) await this.storeCanonical(db, result.entity);
        }
        await this.recompute(db, op.entity_type, op.entity_id, op.household_id);
        return;
      }
      case 'conflict': {
        const row = await this.loadRow(db, op.entity_type, op.entity_id);
        const localState = row ? this.toRecord(row) : null;
        // What the user actually changed: the union of their pending edits (or a deletion).
        const pending = await this.opsFor(db, op.entity_type, op.entity_id);
        const localPatch = pending.some((p) => p.action === 'delete')
          ? { __deleted: true }
          : Object.assign({}, ...pending.filter((p) => p.action === 'update').map((p) => parse(p.payload) ?? {}));
        await db.run(
          `insert or replace into conflicts (id, household_id, entity_type, entity_id, reason, fields, server_entity, local_state, local_patch, created_at)
           values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            result.conflictId,
            op.household_id,
            op.entity_type,
            op.entity_id,
            result.reason,
            JSON.stringify(result.fields),
            result.entity.data ? JSON.stringify(result.entity.data) : null,
            JSON.stringify(localState ?? { __deleted: true }),
            JSON.stringify(localPatch),
            this.now().toISOString(),
          ],
        );
        // The user's version lives in the conflict record now; nothing is lost.
        await db.run(`delete from outbox where entity_type = ? and entity_id = ?`, [op.entity_type, op.entity_id]);
        await this.storeCanonical(db, result.entity);
        await this.recompute(db, op.entity_type, op.entity_id, op.household_id);
        return;
      }
      case 'rejected': {
        await db.run(`update outbox set status = 'failed', last_error = ? where seq = ?`, [JSON.stringify(result.error), op.seq]);
        await this.recompute(db, op.entity_type, op.entity_id, op.household_id);
        return;
      }
    }
  }

  private async cursor(householdId: string): Promise<number> {
    const rows = await this.driver.all<{ value: string }>(`select value from meta where key = ?`, [`cursor:${householdId}`]);
    return rows[0] ? Number(rows[0].value) : 0;
  }

  /**
   * Applies the server change log from the saved cursor; the cursor moves only with a whole page.
   * The screens hear about it once, at the end: a new device's first sync is the whole history
   * (the owner's: 18 pages, 7849 changes), and every notice makes them read and recompute
   * everything again.
   */
  async pull(householdId: string): Promise<void> {
    let cursor = await this.cursor(householdId);
    const touched = new Set<EntityType>();
    try {
      for (;;) {
        let page;
        try {
          page = await this.transport.changes(householdId, cursor, PULL_PAGE);
        } catch (error) {
          if (error instanceof TransportError && error.status === 410) {
            await this.fullResync(householdId);
            return;
          }
          throw error;
        }
        await this.driver.transaction(async (tx) => {
          // Records with unsent changes or a conflict need the full recompute; the others (all of
          // them on a first sync) are stored in one statement each.
          const busy = new Set(
            (
              await tx.all<{ key: string }>(
                `select entity_type || ':' || entity_id as key from outbox union select entity_type || ':' || entity_id from conflicts`,
              )
            ).map((row) => row.key),
          );
          // The clean ones go to SQLite together: one message to the worker instead of one each.
          const clean: SqlStatement[] = [];
          for (const change of page.changes) {
            touched.add(change.entityType);
            if (busy.has(`${change.entityType}:${change.entityId}`)) await this.applyChange(tx, householdId, change);
            else clean.push(this.cleanStatement(householdId, change));
          }
          if (clean.length > 0) await tx.runMany(clean);
          await tx.run(`insert into meta (key, value) values (?, ?) on conflict (key) do update set value = excluded.value`, [
            `cursor:${householdId}`,
            String(page.nextCursor),
          ]);
        });
        cursor = page.nextCursor;
        if (!page.hasMore) return;
      }
    } finally {
      this.emit(touched);
    }
  }

  /**
   * A server change to a record with nothing local on top: what applyChange and recompute would
   * store (canonical and effective state, synced), as one statement instead of six round trips
   * to the SQLite worker. A version older than the one held is ignored, as there.
   */
  private cleanStatement(householdId: string, change: Change): SqlStatement {
    const data = change.op === 'delete' ? null : change.entity;
    return [
      `insert into entities (entity_type, id, household_id, version, deleted, data, local, local_deleted, sync_state, date_key, updated_at)
       values (?, ?, ?, ?, ?, ?, null, 0, 'synced', ?, ?)
       on conflict (entity_type, id) do update set version = excluded.version, deleted = excluded.deleted, data = excluded.data,
         local = null, local_deleted = 0, sync_state = 'synced', date_key = excluded.date_key, updated_at = excluded.updated_at
       where excluded.version >= entities.version`,
      [
        change.entityType,
        change.entityId,
        change.entityType === 'household' ? change.entityId : householdId,
        change.version,
        change.op === 'delete' ? 1 : 0,
        data ? JSON.stringify(data) : null,
        dateKeyOf(change.entityType, data),
        this.now().toISOString(),
      ],
    ];
  }

  private async applyChange(db: SqlExecutor, householdId: string, change: Change): Promise<void> {
    const existing = await this.loadRow(db, change.entityType, change.entityId);
    if (existing && existing.version > change.version) return; // already newer locally
    await this.storeCanonical(db, {
      entityType: change.entityType,
      entityId: change.entityId,
      householdId: change.entityType === 'household' ? change.entityId : householdId,
      version: change.version,
      deleted: change.op === 'delete',
      data: change.entity,
    });
    await this.recompute(db, change.entityType, change.entityId, householdId);
  }

  /**
   * Rebuilds the cache from a snapshot without touching the outbox (SYNC_PROTOCOL §8).
   * Records with local changes survive; others missing from the snapshot are dropped.
   * Interrupted half-way it simply runs again: pages are upserts and the cursor moves last.
   */
  async fullResync(householdId: string): Promise<void> {
    const head = await this.transport.head(householdId);
    const [genRow] = await this.driver.all<{ value: string }>(`select value from meta where key = 'resyncGen'`);
    const generation = Number(genRow?.value ?? 0) + 1;
    await this.driver.run(
      `insert into meta (key, value) values ('resyncGen', ?) on conflict (key) do update set value = excluded.value`,
      [String(generation)],
    );
    for (const entityType of ENTITY_ORDER) {
      let after: string | null = null;
      do {
        const page = await this.transport.snapshot(householdId, entityType, after, 500, null);
        await this.driver.transaction(async (tx) => {
          for (const item of page.items) {
            await this.storeCanonical(tx, item);
            await tx.run(`update entities set gen = ? where entity_type = ? and id = ?`, [generation, item.entityType, item.entityId]);
            await this.recompute(tx, item.entityType, item.entityId, householdId);
          }
        });
        after = page.nextAfter;
      } while (after);
    }
    await this.driver.transaction(async (tx) => {
      await tx.run(
        `delete from entities where household_id = ? and gen < ?
           and not exists (select 1 from outbox o where o.entity_type = entities.entity_type and o.entity_id = entities.id)`,
        [householdId, generation],
      );
      await tx.run(`insert into meta (key, value) values (?, ?) on conflict (key) do update set value = excluded.value`, [
        `cursor:${householdId}`,
        String(head.seq),
      ]);
    });
    this.emit(ENTITY_ORDER);
    await this.pull(householdId);
  }

  // ------------------------------------------------------------------------------ recovery

  async conflicts(householdId: string): Promise<ConflictRecord[]> {
    const rows = await this.driver.all<Record<string, string | null>>(
      `select * from conflicts where household_id = ? order by created_at`,
      [householdId],
    );
    return rows.map((row) => ({
      id: row.id!,
      householdId: row.household_id!,
      entityType: row.entity_type as EntityType,
      entityId: row.entity_id!,
      reason: row.reason!,
      fields: JSON.parse(row.fields!) as string[],
      serverEntity: parse(row.server_entity),
      localState: parse(row.local_state),
      localPatch: parse(row.local_patch),
      createdAt: row.created_at!,
    }));
  }

  /** "Use server version" or "Use my version" (a new operation on the server's current version). */
  async resolveConflict(conflictId: string, choice: 'SERVER' | 'MINE'): Promise<void> {
    const [row] = await this.driver.all<Record<string, string | null>>(`select * from conflicts where id = ?`, [conflictId]);
    if (!row) return;
    const entityType = row.entity_type as WritableEntityType;
    const householdId = row.household_id!;
    const entityId = row.entity_id!;
    let followUp: string | null = null;
    await this.driver.transaction(async (tx) => {
      await tx.run(`delete from conflicts where id = ?`, [conflictId]);
      if (choice === 'MINE') {
        const local = parse(row.local_patch) ?? {};
        const server = parse(row.server_entity);
        const entity = await this.loadRow(tx, entityType, entityId);
        const operationId = newId();
        if (local.__deleted === true) {
          await this.enqueue(tx, {
            operation_id: operationId,
            household_id: householdId,
            entity_type: entityType,
            entity_id: entityId,
            action: 'delete',
            base_version: entity?.version ?? 1,
            payload: null,
            base: server ? JSON.stringify(server) : null,
            created_at: this.now().toISOString(),
          });
          followUp = operationId;
        } else {
          const inputKeys = Object.keys(INPUT_SCHEMAS[entityType].shape);
          // Only the fields the user changed win; untouched fields keep the server's newer values.
          const patch = Object.fromEntries(
            inputKeys.filter((k) => Object.hasOwn(local, k) && !same(local[k], server?.[k])).map((k) => [k, local[k]]),
          );
          const base = Object.fromEntries(inputKeys.map((k) => [k, server?.[k] ?? null]));
          if (Object.keys(patch).length > 0) {
            await this.enqueue(tx, {
              operation_id: operationId,
              household_id: householdId,
              entity_type: entityType,
              entity_id: entityId,
              action: 'update',
              base_version: entity?.version ?? 1,
              payload: JSON.stringify(patch),
              base: JSON.stringify(base),
              created_at: this.now().toISOString(),
            });
            followUp = operationId;
          }
        }
      }
      await this.recompute(tx, entityType, entityId, householdId);
    });
    await this.refreshCounts();
    this.emit([entityType]);
    try {
      await this.transport.resolveConflict(householdId, conflictId, choice === 'SERVER' ? 'SERVER' : 'CLIENT', followUp);
    } catch {
      // Best effort: the server-side record stays open until a later resolution call.
    }
  }

  async failedOperations(): Promise<FailedOperation[]> {
    const rows = await this.driver.all<OutboxRow>(`select * from outbox where status = 'failed' order by seq`);
    return rows.map((row) => ({
      operationId: row.operation_id,
      entityType: row.entity_type,
      entityId: row.entity_id,
      action: row.action,
      error: row.last_error ? (JSON.parse(row.last_error) as { code: string; message: string }) : null,
    }));
  }

  /** Manual retry of failed operations. */
  async retryFailed(): Promise<void> {
    await this.driver.run(`update outbox set status = 'queued', attempts = 0, next_attempt_at = null where status = 'failed'`);
    await this.refreshCounts();
  }

  /** Drops the local changes of one record (e.g. after the server refused them). */
  async discardLocalChanges(entityType: EntityType, entityId: string): Promise<void> {
    await this.driver.transaction(async (tx) => {
      const row = await this.loadRow(tx, entityType, entityId);
      await tx.run(`delete from outbox where entity_type = ? and entity_id = ?`, [entityType, entityId]);
      await this.recompute(tx, entityType, entityId, row?.household_id ?? '');
    });
    await this.refreshCounts();
    this.emit([entityType]);
  }

  async entityState(entityType: EntityType, id: string): Promise<EntitySyncState | null> {
    const row = await this.loadRow(this.driver, entityType, id);
    return row?.sync_state ?? null;
  }

  /** Sets a transport-level retry to "now" (network came back). */
  async wakeQueued(): Promise<void> {
    await this.driver.run(`update outbox set next_attempt_at = null where status = 'queued'`);
  }
}
